import type { DayOfWeek } from "@/types/meal-plan-state";

/** Get the Monday ISO date string (YYYY-MM-DD) for any given date */
export function getMonday(date: Date): string {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun, 1=Mon, ..., 6=Sat
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return formatDate(d);
}

/** Format a Date as YYYY-MM-DD */
function formatDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Today's local date as YYYY-MM-DD */
export function getTodayDate(): string {
  return formatDate(new Date());
}

/** A week id is the ISO date of a Monday ("2026-10-19"), as `getMonday` builds it. */
export function isValidWeekId(weekId: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(weekId);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  // Round-trips (no "2026-02-30") and is a Monday.
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d && date.getUTCDay() === 1;
}

/** Get today's week ID (Monday date) */
export function getCurrentWeekId(): string {
  return getMonday(new Date());
}

/** Add n weeks to a weekId, returning a new weekId */
export function addWeeks(weekId: string, n: number): string {
  const d = new Date(weekId + "T00:00:00");
  d.setDate(d.getDate() + n * 7);
  return formatDate(d);
}

/** Parse a weekId into a Date */
function parseWeekId(weekId: string): Date {
  return new Date(weekId + "T00:00:00");
}

/** Format a week range for display: "16 - 22 Mar 2026" */
export function formatWeekRange(weekId: string): string {
  const monday = parseWeekId(weekId);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);

  const months = [
    "Ene", "Feb", "Mar", "Abr", "May", "Jun",
    "Jul", "Ago", "Sep", "Oct", "Nov", "Dic",
  ];

  const monDay = monday.getDate();
  const sunDay = sunday.getDate();
  const monMonth = months[monday.getMonth()]!;
  const sunMonth = months[sunday.getMonth()]!;
  const year = sunday.getFullYear();

  if (monday.getMonth() === sunday.getMonth()) {
    return `${monDay} - ${sunDay} ${monMonth} ${year}`;
  }
  return `${monDay} ${monMonth} - ${sunDay} ${sunMonth} ${year}`;
}

/** Get all Monday weekIds that have days overlapping a given month */
export function getWeeksInMonth(year: number, month: number): string[] {
  // First day of the month
  const firstOfMonth = new Date(year, month, 1);
  // Last day of the month
  const lastOfMonth = new Date(year, month + 1, 0);

  // Find the Monday of the week containing the first day
  const startMonday = parseWeekId(getMonday(firstOfMonth));

  const weeks: string[] = [];
  const current = new Date(startMonday);

  // Keep adding weeks while the Monday is before or on the last day of month
  // (or the week overlaps the month)
  while (current <= lastOfMonth) {
    weeks.push(formatDate(current));
    current.setDate(current.getDate() + 7);
  }

  return weeks;
}

/** Get the dates (YYYY-MM-DD) for each day Mon-Sun in a given week */
export function getWeekDates(weekId: string): { day: DayOfWeek; date: string; dayNum: number }[] {
  const days: DayOfWeek[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const monday = parseWeekId(weekId);

  return days.map((day, i) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + i);
    return { day, date: formatDate(d), dayNum: d.getDate() };
  });
}

/** Get today's DayOfWeek, or null if today is not in the given week */
export function getTodayDayOfWeek(weekId: string): DayOfWeek | null {
  const today = new Date();
  const monday = new Date(weekId + "T00:00:00");
  const diffMs = today.getTime() - monday.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0 || diffDays > 6) return null;
  const days: DayOfWeek[] = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  return days[diffDays]!;
}

/** Get month name and year for display */
export function formatMonthYear(year: number, month: number): string {
  const months = [
    "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
    "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
  ];
  return `${months[month]} ${year}`;
}
